import { useCallback, useEffect, useState } from 'react'
import { GRAPHQL_ENDPOINT } from '@/app/config/settings'

export type CarConditionCatalogEntry = {
  id: string
  system: string
  subsystem: string
  component?: string | null
  conditionType?: string | null
  extent?: string | null
  isDamage: boolean
  targetFlow: 'CARLA' | 'AMS' | 'UNKNOWN'
}

const CATALOG_QUERY = `
  query CarConditionCatalog {
    carConditionCatalog {
      id
      system
      subsystem
      component
      conditionType
      extent
      isDamage
      targetFlow
    }
  }
`

export function catalogEntryLabel(e: CarConditionCatalogEntry): string {
  // Connect all parts with '→'
  return [e.system, e.subsystem, e.component, e.conditionType, e.extent].filter(Boolean).join(' → ')
}

export function useCarConditionCatalog(getFirebaseToken: () => Promise<string | null>) {
  const [data, setData] = useState<CarConditionCatalogEntry[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const fetchCatalog = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const token = await getFirebaseToken()
      const response = await fetch(GRAPHQL_ENDPOINT!, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          query: CATALOG_QUERY,
          operationName: 'CarConditionCatalog',
        }),
      })
      if (!response.ok) {
        throw new Error(`Failed to fetch catalog: ${response.status}`)
      }
      const json = await response.json()
      if (json.errors?.length) {
        throw new Error(json.errors[0]?.message ?? 'GraphQL error')
      }
      setData(json.data?.carConditionCatalog ?? [])
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch catalog'))
    } finally {
      setIsLoading(false)
    }
  }, [getFirebaseToken])

  useEffect(() => {
    fetchCatalog()
  }, [fetchCatalog])

  return { data, isLoading, error, refresh: fetchCatalog }
}
